import { EMPTY_DOC } from './types.js';
import { isContainerType } from './registry.js';

/**
 * Collect all block ids that are descendants of `id` (inclusive).
 * @param {object} doc
 * @param {string} id
 * @returns {string[]}
 */
export function subtreeIds(doc, id) {
  const ids = [id];
  const block = doc.blocks[id];
  if (block && Array.isArray(block.children)) {
    for (const childId of block.children) {
      ids.push(...subtreeIds(doc, childId));
    }
  }
  return ids;
}

/**
 * Return the parent container id and index for a block id, or null if it's a
 * top-level block.
 * @param {object} doc
 * @param {string} id
 * @returns {{ parentId: string|null, index: number }|null}
 */
export function findParent(doc, id) {
  // Check root
  const rootIdx = doc.root.indexOf(id);
  if (rootIdx !== -1) return { parentId: null, index: rootIdx };

  // Check each container block
  for (const block of Object.values(doc.blocks)) {
    if (Array.isArray(block.children)) {
      const idx = block.children.indexOf(id);
      if (idx !== -1) return { parentId: block.id, index: idx };
    }
  }
  return null;
}

/**
 * Remove stale or orphaned entries and ensure every id referenced in root /
 * children actually exists in blocks.
 * @param {object} doc
 * @returns {object} cleaned doc
 */
export function normalizeDoc(doc) {
  if (!doc || typeof doc !== 'object') return { ...EMPTY_DOC };

  const blocks = doc.blocks && typeof doc.blocks === 'object' ? { ...doc.blocks } : {};
  const root = Array.isArray(doc.root) ? doc.root : [];

  // Remove ids that reference non-existent blocks
  const cleanList = (list) => (Array.isArray(list) ? list.filter((id) => id in blocks) : []);

  const cleanedRoot = cleanList(root);

  // Recursively clean children
  const cleanBlock = (id) => {
    const block = blocks[id];
    if (!block) return;
    if (Array.isArray(block.children)) {
      blocks[id] = { ...block, children: cleanList(block.children) };
      blocks[id].children.forEach(cleanBlock);
    }
  };
  cleanedRoot.forEach(cleanBlock);

  // Remove unreachable blocks
  const reachable = new Set();
  const visit = (id) => {
    reachable.add(id);
    const block = blocks[id];
    if (block && Array.isArray(block.children)) {
      block.children.forEach(visit);
    }
  };
  cleanedRoot.forEach(visit);

  const cleanedBlocks = {};
  for (const id of reachable) {
    cleanedBlocks[id] = blocks[id];
  }

  return { root: cleanedRoot, blocks: cleanedBlocks };
}

/**
 * Deep-clone a doc object (serialization round-trip).
 */
export function cloneDoc(doc) {
  return JSON.parse(JSON.stringify(doc));
}

/**
 * Return true if `ancestor` contains `descendant` in its subtree.
 */
export function isAncestor(doc, ancestorId, descendantId) {
  const block = doc.blocks[ancestorId];
  if (!block || !Array.isArray(block.children)) return false;
  if (block.children.includes(descendantId)) return true;
  return block.children.some((childId) => isAncestor(doc, childId, descendantId));
}

/**
 * Ensure every container block that is registered as a container has a
 * children array.  Non-container blocks must not have one.
 */
export function coerceContainers(doc) {
  const blocks = { ...doc.blocks };
  for (const [id, block] of Object.entries(blocks)) {
    if (isContainerType(block.type)) {
      if (!Array.isArray(block.children)) {
        blocks[id] = { ...block, children: [] };
      }
    } else {
      if ('children' in block) {
        const { children: _children, ...rest } = block;
        blocks[id] = rest;
      }
    }
  }
  return { ...doc, blocks };
}
